import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import format from 'date-fns/format'
import styles from 'styles/components/card.module.scss'
import text from 'styles/utility/text.module.scss'
import { Caution } from 'components/Typography'
import { OrderType } from 'types/checkout'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { Checkbox } from 'components/Form'
import { OrangeButton } from 'components/Button'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getRole, getStoreRef, getUid } from 'reducks/auth/selectors'
import { toggleDelivered, togglePrepared } from 'lib/order'
import {
  DeliveryOrderCardBodySummary,
  OrderCardCancel,
  DeliveryOrderCardBodyItems,
  DeliveryOrderCardAddress,
} from 'components/Card'
import { Role } from 'lib/role'
import { useCanBeCanceled, useCancelDeadline, useOrderLabel } from 'hooks/order'
import { getLastPickupTime } from 'reducks/store/selectors'
import { BusinessTimeType } from 'types/store'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { parseTimeToDate, parseToDate } from 'lib/helper'
import { getBusinessTimeOnDay } from 'lib/businessHour'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { StoreShippingType } from 'types/storeShipping'
import { DeliveryConfirmationModal } from 'components/Modal'
import { useModal } from 'hooks/modal'
import { DELIVERY_FEE_TAX_RATE } from 'constants/tax'
import classNames from 'classnames'
import { useNumericalString } from 'hooks/form'
const daysOfWeek = new DaysOfWeek()

type Props = {
  isSearch?: boolean
  order: OrderType
  storeShipping: StoreShippingType
}

const DeliveryOrderCardBody: FC<Props> = (props) => {
  const { isSearch, order, storeShipping } = props

  const role = useSelector((state: State) => getRole(state))
  const lastPickupTime: BusinessTimeType = useSelector((state: State) => getLastPickupTime(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const uid = useSelector((state: State) => getUid(state))
  const roleManager = new Role(role, storeRef)
  const dayOfWeek = daysOfWeek.getDayOfWeek(parseToDate(order.date), specialHolidays)
  const lastPickupTimeOnDay =
    getBusinessTimeOnDay(dayOfWeek, lastPickupTime, order.date, storeCalendars, 'last_pickup_time') ?? '23:45'

  const { open, closeModal, openModal } = useModal()
  const [isDelivered, setDelivered] = useState<boolean>(order.is_delivered)
  const [isPrepared, setPrepared] = useState<boolean>(order.is_prepared)
  const [shippedAt, setShippedAt] = useState<Date | null>(null)
  const {
    state: trackingNumber,
    handleState: handleTrackingNumber,
    setState: setTrackingNumber,
  } = useNumericalString('')

  const cancelDeadline = isSearch
    ? useMemo(() => new Date(), [])
    : useCancelDeadline(
        order.checkout_at ? order.checkout_at.toDate() : order.updated_at.toDate(),
        parseTimeToDate(lastPickupTimeOnDay, parseToDate(order.date)),
        order.payment_method,
        uid
      )
  const canBeCanceled = isSearch ? false : useCanBeCanceled(cancelDeadline, isDelivered, order.status, order.type)

  const handlePrepared = useCallback(
    (checkoutId: string) => {
      setPrepared((prevState) => {
        togglePrepared(checkoutId, !prevState, storeRef)
        return !prevState
      })
    },
    [setPrepared, storeRef]
  )

  const handleDelivered = useCallback(() => {
    setDelivered((prevState) => {
      if (!order.checkout_id) return prevState
      toggleDelivered(order.checkout_id, !prevState, storeRef)
      return !prevState
    })
  }, [order, setDelivered, storeRef])

  const orderLabel = useOrderLabel(order.checkout_at, order.updated_at)
  const deliveryFeeAmount = Math.floor(
    (storeShipping.delivery_fee.delivery_fee_amount + storeShipping.delivery_fee.frozen_fee_amount) *
      DELIVERY_FEE_TAX_RATE
  )

  useEffect(() => {
    setShippedAt(storeShipping.shipped_at?.toDate() || null)
    setTrackingNumber(storeShipping.tracking_number || '')
  }, [storeShipping])

  return (
    <div className={styles.order__body}>
      {order.status === 'canceled' && (
        <>
          <FlexRow justifyContent={'center'}>
            <Caution caution={'この注文はキャンセルされています'} size={'large'} />
          </FlexRow>
          <Spacer size={16} />
        </>
      )}
      <FlexRow justifyContent={'between'}>
        <div>
          <FlexRow justifyContent={'start'}>
            <img src={'/icon/grey/clock.svg'} alt={'お届け希望日'} />
            <Spacer size={5} />
            <span className={styles.order__body__pickup__label}>お届け希望日：</span>
            <span className={styles.order__body__pickup__time}>
              {format(parseToDate(storeShipping.scheduled_delivery_date), 'yyyy年M月d日')}
            </span>
          </FlexRow>
          <Spacer size={8} />
          <FlexRow justifyContent={'start'}>
            <img src={'/icon/grey/clock.svg'} alt={'お届け希望時間'} />
            <Spacer size={5} />
            <span className={styles.order__body__pickup__label}>お届け希望時間：</span>
            <span className={styles.order__body__pickup__time}>{storeShipping.scheduled_delivery_time}</span>
          </FlexRow>
        </div>
        <Checkbox
          disabled={!roleManager.hasEnoughRole(5) || order.status === 'canceled'}
          id={order.checkout_id ?? ''}
          isChecked={isPrepared}
          label={'準備済'}
          handleCheckbox={handlePrepared}
        />
      </FlexRow>
      <Spacer size={8} />
      <DeliveryOrderCardBodyItems
        isGift={storeShipping.is_gift}
        items={order.items}
        sender_name={storeShipping.sender_name}
      />
      <Spacer size={8} />
      <DeliveryOrderCardAddress {...storeShipping.delivery_address} />
      <Spacer size={8} />
      <DeliveryOrderCardBodySummary
        amount={order.amount}
        checkoutChannel={order.checkout_channel}
        deliveryCompany={storeShipping.delivery_company}
        discountAmount={order.discount_amount || 0}
        deliveryFeeAmount={deliveryFeeAmount}
        isFrozenDelivery={storeShipping.delivery_fee.is_frozen_delivery}
        orderLabel={orderLabel}
        request={order.request}
      />
      <Spacer size={16} />
      <OrangeButton
        disabled={isDelivered || !roleManager.hasEnoughRole(5) || order.status === 'canceled'}
        label={'商品を発送しました'}
        onClick={openModal}
        size={'medium'}
        type={'main'}
      />
      {isDelivered && (
        <>
          <Spacer size={16} />
          <p className={classNames(text.font_bold, text.align_center)}>
            {shippedAt && (
              <>
                発送日時：{format(shippedAt, 'yyyy年MM月dd日 HH:mm')}
                <br />
              </>
            )}
            追跡番号：<span className={classNames(text.color_orange_main)}>{trackingNumber}</span>
          </p>
        </>
      )}
      <DeliveryConfirmationModal
        closeModal={closeModal}
        handleDelivered={handleDelivered}
        handleTrackingNumber={handleTrackingNumber}
        open={open}
        setShippedAt={setShippedAt}
        storeShippingRef={storeShipping.ref}
        trackingNumber={trackingNumber}
      />
      {canBeCanceled && <OrderCardCancel cancelDeadline={cancelDeadline} checkoutId={order.checkout_id ?? ''} />}
    </div>
  )
}

export default DeliveryOrderCardBody
